import {
  FulfillmentTypeListResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { useBasketContext } from "../../../hooks";
import { useState } from "react";
import { useStockListOrderSummaryProps } from "../useStockListOrderSummaryProps";
import { formatCurrency, pluralize } from "@sellar-io/common";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { twMerge } from "tailwind-merge";
import { Icon } from "@sellar-io/components";

export const HaulierTierInfoCard = ({
  fulfillmentType,
  compact = false,
  supplierStorefront,
}: {
  fulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  compact?: boolean;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
}) => {
  const [{ order }] = useBasketContext();
  const [active, setActive] = useState(false);

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { deliveryCharge },
  } = useStockListOrderSummaryProps({
    order: order,
    deliveryChargeRules: fulfillmentType?.DeliveryChargeRules,
    deliveryCharge: fulfillmentType?.charge,
    deliveryChargeCap: fulfillmentType?.deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges:
      fulfillmentType?.FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    freeDeliveryBasketSubtotal,
  });

  const numberOfOrderItems = order?.OrderItems?.reduce((acc, item) => {
    return acc + item.quantity;
  }, 0);

  const perItemCost = deliveryCharge / (numberOfOrderItems ?? 1);
  const perItemCostPlus1 = deliveryCharge / ((numberOfOrderItems ?? 1) + 1);
  const perItemCostPlus2 = deliveryCharge / ((numberOfOrderItems ?? 1) + 2);

  return (
    <>
      <div
        className={twMerge(
          "flex cursor-pointer items-center justify-between rounded-lg bg-zinc-100 hover:bg-zinc-200",
          compact ? "p-2" : "p-4",
        )}
        onClick={() => setActive(true)}
      >
        <div className="flex flex-col gap-2">
          {!compact && (
            <>
              <span className="font-semibold">Pallet delivery costs</span>
              <span>{formatCurrency(perItemCost)} per item</span>
            </>
          )}
          <div className="flex w-full gap-1 overflow-hidden text-sm text-nowrap text-ellipsis">
            <span className="font-semibold">+1 item</span>
            <span className="text-emerald-600">
              {formatCurrency(perItemCostPlus1)}
            </span>
            <span className="font-semibold">, +2 items</span>
            <span className="text-emerald-600">
              {formatCurrency(perItemCostPlus2)}
            </span>
            <span className="font-semibold">...</span>
          </div>
        </div>
        <Icon icon="CHEVRON_RIGHT" size={1} />
      </div>
      <HaulierInfoDialog
        active={active}
        onDismiss={() => setActive(false)}
        numberOfOrderItems={numberOfOrderItems ?? 1}
        deliveryCharge={deliveryCharge}
      />
    </>
  );
};

const HaulierInfoDialog = ({
  active,
  onDismiss,
  numberOfOrderItems,
  deliveryCharge,
}: {
  active: boolean;
  onDismiss: () => void;
  numberOfOrderItems: number;
  deliveryCharge: number;
}) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        leftIcon={"CHEVRON_DOWN"}
        title="Pallet usage"
        description={`Reduce your delivery costs by adding more items `}
      />
      <DialogContent className="flex flex-col gap-4">
        <div className="flex w-full flex-col gap-2 rounded-lg border border-zinc-300 bg-zinc-50 p-4">
          <span className="text-sm">Delivery cost (flat charge)</span>
          <span className="font-semibold">
            {formatCurrency(deliveryCharge)}
          </span>
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg border border-zinc-300 p-4">
          <div className="flex w-full flex-col gap-2 rounded-lg bg-zinc-50 p-4">
            <span className="text-sm font-semibold">Pallet delivery costs</span>
            <span>
              {formatCurrency(deliveryCharge / numberOfOrderItems)} per item
            </span>
            <span className="text-sm">
              Your basket:{" "}
              <span className="font-semibold">
                {numberOfOrderItems} {pluralize("item", numberOfOrderItems)}
              </span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            {Array.from({ length: 10 }).map((_, i) => {
              const number = i + 1;
              const price = formatCurrency(deliveryCharge / number);

              return (
                <div
                  key={i}
                  className={twMerge(
                    "flex w-full justify-between rounded-lg p-2",
                    number === numberOfOrderItems &&
                      "border border-zinc-300 bg-zinc-50",
                  )}
                >
                  <span>
                    {number} {pluralize("item", number)}{" "}
                    <span className="text-zinc-500">
                      {number > numberOfOrderItems
                        ? `(+${number - numberOfOrderItems})`
                        : ""}
                    </span>
                  </span>
                  <span
                    className={twMerge(
                      number > numberOfOrderItems && "text-emerald-600",
                    )}
                  >
                    {price}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
